import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { findSkinImage, buildStickerIndex } from './skinIndex';
import { withStickerImages } from './helpers';
import StickerSlots from './StickerSlots';
import ItemHistoryTimeline from './ItemHistoryTimeline';
import './NewItemsPrompt.css';

// Banner + modal for inventory assets the extension has seen but the tracker
// doesn't hold yet. Both wear either theme: pass variant="zone" for the paper
// skin, "classic" for the dark one. All styling lives in NewItemsPrompt.css.
//
// PICTURES
// A sale read off a marketplace carries no icon — CSFloat's history gives the
// skin name, the wear, the float and the money, and nothing to draw with. So
// every picture on these screens falls back to the skins database by name and
// condition, the same lookup the History tab uses for the same entries. Steam
// inventory items DO arrive with an icon, so those keep using it and only fall
// back when it is missing.

const EXTERIOR_TO_CONDITION = {
  'Factory New':    'Factory New',
  'Minimal Wear':   'Minimal Wear',
  'Field-Tested':   'Field Tested',
  'Well-Worn':      'Well Worn',
  'Battle-Scarred': 'Battle Scarred',
};

function stripCondition(name) {
  return (name || '').replace(/ \((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/, '').trim();
}

// How big one sticker slot is in the untracked-sales list, per theme.
//
// Sized so a FOUR-sticker strip is 200px wide, which is the width these were
// asked to be. Everything else follows from that: the gap is whatever the
// theme's own strip already uses (.phi-sticker-row is 4px, .zn-inv-stickers is
// 5px), so
//
//   slot = (200 - 3 * gap) / 4
//
// and a strip of n slots is `n * slot + (n - 1) * gap`. Held here rather than
// in the stylesheet because the width has to be computed per row from the
// number of stickers — CSS cannot count them, and a fixed 200px would stretch
// a lone sticker across the whole strip.
const STICKER_SLOT = {
  classic: { slot: 47, gap: 4 },
  zone:    { slot: 46, gap: 5 },
};

// Stickers reach the app spelled two ways. An inventory item names them bare
// ("sdy (Gold) | London 2018"); a marketplace entry keeps the full market name
// ("Sticker | sdy (Gold) | London 2018"). StickerSlots looks a price up as
// `Sticker | ${name}`, so the prefixed form would ask for "Sticker | Sticker |
// …" and silently find nothing. Trimmed here, at the one place that holds the
// marketplace spelling, rather than teaching every consumer both forms.
function bareStickerNames(stickers) {
  return (stickers || [])
    .filter(s => s?.name)
    .map(s => ({ ...s, name: String(s.name).replace(/^Sticker \| /, '') }));
}

export function NewItemsBanner({ count, onReview, onDismiss, variant = 'classic' }) {
  return (
    <div className={`nip-banner ${variant}`}>
      <span className="nip-banner-ico" aria-hidden="true">{variant === 'zone' ? 'New' : ''}</span>
      <span className="nip-banner-txt">
        You have <strong>{count} new item{count !== 1 ? 's' : ''}</strong> in your inventory.{' '}
        <button type="button" className="nip-banner-link" onClick={onReview}>
          Click here to review
        </button>
      </span>
      <button type="button" className="nip-banner-x" onClick={onDismiss} aria-label="Dismiss">×</button>
    </div>
  );
}

// Announces rows the server moved out of Live on its own, because a settled
// CSFloat sale proved they were already sold.
//
// It names them rather than just counting them, and it is dismissed rather than
// actioned: the move has already happened, and a change to your own books that
// you cannot see is worse than no automation at all. The second line carries
// what could NOT be settled automatically — sales still inside trade protection,
// and sold items that were never tracked and so have no cost basis to net
// against — which is the honest boundary of what this can do for you.
export function AutoSoldBanner({
  applied = [], inFlight = [], unmatched = [], autoFiled = [], onReview, onDismiss, variant = 'classic',
}) {
  if (!applied.length && !unmatched.length && !inFlight.length && !autoFiled.length) return null;

  const list = (entries) => {
    const names = entries.map(a => a.name).filter(Boolean);
    return { shown: names.slice(0, 3).join(', '), extra: names.length - 3 };
  };

  const { shown, extra } = list(applied);
  // Rows that did not exist until this pass wrote them, from a CSFloat purchase
  // and a CSFloat sale of the same item. Named separately from `applied`
  // because they are a different event: nothing moved out of Live, a position
  // you were never tracking was closed and booked in one go. Being told is the
  // whole point — see the note above.
  const filed = list(autoFiled);
  const filedProfit = autoFiled.reduce((s, a) => s + (Number(a.profit) || 0), 0);

  return (
    <div className={`nip-banner sold ${variant}`}>
      <span className="nip-banner-ico" aria-hidden="true">{variant === 'zone' ? 'Sold' : ''}</span>
      <span className="nip-banner-txt">
        {applied.length > 0 && (
          <>
            Moved <strong>{applied.length} item{applied.length !== 1 ? 's' : ''}</strong> to Sold
            {shown && <>: {shown}{extra > 0 ? ` and ${extra} more` : ''}</>}
            . Confirmed sold on CSFloat.
          </>
        )}
        {autoFiled.length > 0 && (
          <>
            {applied.length > 0 && ' '}
            Filed <strong>{autoFiled.length} sold item{autoFiled.length !== 1 ? 's' : ''}</strong>
            {filed.shown && <>: {filed.shown}{filed.extra > 0 ? ` and ${filed.extra} more` : ''}</>}
            {' '}with what you paid on CSFloat
            {filedProfit !== 0 && (
              <> ({filedProfit >= 0 ? '+' : '-'}${Math.abs(filedProfit).toFixed(2)})</>
            )}.
          </>
        )}
        {(unmatched.length > 0 || inFlight.length > 0) && (
          <span className="nip-banner-sub">
            {unmatched.length > 0 && (
              <>
                {unmatched.length} sold item{unmatched.length !== 1 ? 's are' : ' is'} not tracked yet.
                {onReview && <> <button type="button" className="nip-banner-link" onClick={onReview}>Add {unmatched.length !== 1 ? 'them' : 'it'}</button>.</>}
                {inFlight.length > 0 ? ' ' : ''}
              </>
            )}
            {inFlight.length > 0 && (
              <>{inFlight.length} sale{inFlight.length !== 1 ? 's are' : ' is'} still settling on CSFloat and stay{inFlight.length !== 1 ? '' : 's'} in Live until then.</>
            )}
          </span>
        )}
      </span>
      <button type="button" className="nip-banner-x" onClick={onDismiss} aria-label="Dismiss">×</button>
    </div>
  );
}

// Sales the tracker can prove happened, on items it never held a row for.
//
// Everything about these is already known: which item, what it sold for, when,
// and on which marketplace. The one thing nobody but you knows is what you paid
// for it, so that is the only thing this asks. It deliberately does NOT route
// through the Steam import flow: that fetches a whole inventory to find items
// you still own, and these are items you no longer own.
//
// "Not mine" exists because a proven sale is not the same as a sale you want in
// your books. Something bought and flipped outside the tracker on purpose
// should be dismissable without inventing a cost basis for it.
export function UntrackedSalesModal({
  sales = [], rows = [], skinsData = [], livePrices = null,
  onAdd, onAddMany, onCloseRow, onIgnore, onClose, variant = 'classic',
}) {
  const [buyPrices, setBuyPrices] = useState({});
  const [targets, setTargets] = useState({});
  // One open at a time. These are read one after another, and several timelines
  // unfolded at once turns the list into a page you have to scroll to file
  // anything.
  const [openHistory, setOpenHistory] = useState(null);

  // "Untracked" here means "no row matched this item by IDENTITY" — assetId, or
  // float plus paint seed. That is not the same as "you were not holding one".
  // Rows created from a Steam trade carry neither, so a genuinely tracked item
  // can land in this list, and adding it as new would leave the original in
  // Live and the same item counted twice.
  //
  // So anything that could plausibly be it is offered by name and condition.
  // That is too weak a signal for the server to act on by itself, which is why
  // it never does — but it is exactly the right thing to put in front of the
  // one person who knows: you.
  const candidatesFor = useMemo(() => {
    const byTrade = {};
    for (const sale of sales) {
      byTrade[sale.tradeId] = rows
        .map((r, i) => ({ r, i }))
        .filter(({ r }) =>
          r.skin?.value === sale.name
          && !(parseFloat(r.sellPrice) > 0)
          && !r.tradeId
          && (!sale.condition || !r.condition?.value || r.condition.value === sale.condition));
    }
    return byTrade;
  }, [sales, rows]);

  // What one row would do if you pressed its button — resolved in one place so
  // the row and the "add everything priced" button in the footer can never
  // disagree about which sales are ready or what they would file.
  const decide = useCallback((sale) => {
    const candidates = candidatesFor[sale.tradeId] || [];
    // A stored choice is re-checked against the current candidates rather than
    // trusted: filing one sale can close a row another sale was pointing at.
    const stored = targets[sale.tradeId];
    const valid  = stored === 'new' || candidates.some(c => String(c.i) === stored);
    // Default to closing an existing row when there is one: the common case is
    // that the item WAS tracked and just could not be proved, and defaulting to
    // "add new" would make duplicates the easy path.
    const target = valid ? stored : (candidates.length ? String(candidates[0].i) : 'new');
    const chosen = target === 'new' ? null : candidates.find(c => String(c.i) === target);

    const raw   = buyPrices[sale.tradeId];
    const typed = parseFloat(raw);
    const priced = Number.isFinite(typed) && raw !== '' && raw !== undefined;

    return { candidates, target, chosen, raw, typed, priced };
  }, [candidatesFor, targets, buyPrices]);

  // The sales the footer button would file: priced by hand, and not pointing at
  // an existing row.
  //
  // Rows with a live-row candidate are deliberately LEFT OUT even though their
  // field shows a figure. That figure is the candidate row's own cost, and
  // filing it in bulk would silently close whichever row the dropdown happened
  // to default to. Which of two identical Redlines a sale belongs to is a
  // judgement, and the whole point of that dropdown is that only you can make
  // it — so those stay one click each.
  const readyToAdd = useMemo(
    () => sales
      .map(sale => ({ sale, d: decide(sale) }))
      .filter(({ d }) => !d.chosen && d.priced),
    [sales, decide]
  );

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const money = n => `$${Number(n || 0).toFixed(2)}`;

  const addAllPriced = () => {
    if (!readyToAdd.length) return;
    const entries = readyToAdd.map(({ sale, d }) => ({ sale, buyPrice: d.typed }));
    // One call, so forty rows are one state update and one request. Falls back
    // to the single-item handler if the caller has not wired the bulk one.
    if (onAddMany) onAddMany(entries);
    else entries.forEach(e => onAdd?.(e.sale, e.buyPrice));
    // The filed sales leave the list; their typed figures go with them, so a
    // tradeId that somehow returns starts blank instead of wearing an old price.
    setBuyPrices(prev => {
      const next = { ...prev };
      for (const { sale } of readyToAdd) delete next[sale.tradeId];
      return next;
    });
  };

  // Sticker pictures for the ones CSFloat did not send an image for. Built once
  // for the whole list rather than per row — the index is a pass over the entire
  // skins database, and this modal can hold seventy-five rows.
  const stickerIndex = useMemo(() => buildStickerIndex(skinsData), [skinsData]);

  const body = (
    <div
      className={`nip-overlay ${variant}`}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="nip-panel" role="dialog" aria-modal="true" aria-label="Untracked sales">
        <div className="nip-head">
          <div className="nip-head-txt">
            <div className="nip-title">Sold, but never tracked</div>
            <div className="nip-sub">
              {sales.length} item{sales.length !== 1 ? 's' : ''} sold on CSFloat that never had a
              row in the tracker. Enter what you paid to record the profit.
            </div>
          </div>
          <button type="button" className="nip-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="nip-body">
          {sales.length === 0 ? (
            <div className="nip-empty">Nothing left to file.</div>
          ) : sales.map(sale => {
            // Everything that reaches this list needs a basis typed in. A sale
            // whose cost the account could prove — one bought on CSFloat — is
            // filed automatically by the reconciler and never appears here, so
            // there is no known figure to offer.
            const { candidates, target, chosen, raw, typed } = decide(sale);
            // Closing an existing row uses that row's own cost basis; only a
            // brand new row needs one supplied here.
            const buy   = chosen ? (parseFloat(chosen.r.buyPrice) || 0) : typed;
            const known = chosen ? true : (Number.isFinite(typed) && raw !== '');

            // Same 2% sales + 1% withdrawal cut the reconciler applies, so the
            // number previewed here is the number that gets written.
            const profit = known ? (sale.price * 0.97) - buy : null;

            const open = openHistory === sale.tradeId;

            // Sticker strip sizing — see STICKER_SLOT above for the numbers.
            //
            // A slot is `flex: 1` and the classic one is `aspect-ratio: 1`, so a
            // strip with no width takes the pictures' intrinsic size and the
            // square height follows it — which is how these first came out the
            // size of the row. Every other caller passes maxWidth for exactly
            // this reason; this one computes it from the number of slots, so one
            // sticker stays one sticker wide instead of being stretched across
            // the space four would need.
            const stickers  = withStickerImages(bareStickerNames(sale.stickers), stickerIndex);
            const slotCount = Math.min(5, stickers.length);
            const { slot: slotPx, gap } = STICKER_SLOT[variant] || STICKER_SLOT.classic;
            const stripWidth = slotCount * slotPx + (slotCount - 1) * gap;

            return (
              <div className="nip-sale-wrap" key={sale.tradeId}>
                <div className="nip-sale">
                  {/* Picture and name open the item's history. Everything to the
                      right of them is a control, so the clickable region stops
                      where the filing decisions start. */}
                  <button
                    type="button"
                    className="nip-sale-open"
                    aria-expanded={open}
                    onClick={() => setOpenHistory(open ? null : sale.tradeId)}
                  >
                    {(() => {
                      // CSFloat sales never carry an icon, so the database is the
                      // only source of a picture here — not a fallback in practice,
                      // the normal path.
                      const img = sale.iconUrl || findSkinImage(skinsData, sale.name, sale.condition);
                      return img
                        ? <img src={img} alt="" className="nip-sale-img" loading="lazy" />
                        : <div className="nip-sale-img nip-img-blank" />;
                    })()}

                    <span className="nip-sale-txt">
                      <span className="nip-sale-name">{sale.name}</span>
                      <span className="nip-sale-meta">
                        {sale.condition || 'No condition'} · Sold {money(sale.price)} on CSFloat
                        {sale.soldAt && <> · {new Date(sale.soldAt).toLocaleDateString()}</>}
                        <span className="nip-sale-more">{open ? 'Hide history' : 'History'}</span>
                      </span>
                      {/* The float and the pattern, on their own line. Four
                          decimals is what the History tab shows, and it is the
                          precision people actually compare on. */}
                      {(sale.float != null || sale.paintSeed != null) && (
                        <span className="nip-sale-wear">
                          {sale.float != null && (
                            <span className="nip-sale-float">{Number(sale.float).toFixed(4)}</span>
                          )}
                          {sale.paintSeed != null && (
                            <span className="nip-sale-seed">Pattern {sale.paintSeed}</span>
                          )}
                        </span>
                      )}
                    </span>
                  </button>

                  {/* Outside the button on purpose: a sticker slot is itself
                      clickable (it copies the name), and interactive markup
                      nested in a button is neither valid nor reachable by
                      keyboard. hideWhenEmpty keeps stickerless rows the shape
                      they already had. */}
                  {slotCount > 0 && (
                    <StickerSlots
                      stickers={stickers}
                      prices={livePrices}
                      variant={variant}
                      max={slotCount}
                      maxWidth={stripWidth}
                      className="nip-sale-stickers"
                      // Height inline alongside the width so the two cannot
                      // drift: the zone strip carries a height of its own in
                      // ZoneTheme.css, and a stylesheet that set one without
                      // the other would give non-square slots.
                      style={{ width: stripWidth, height: slotPx }}
                    />
                  )}

                  <label className="nip-sale-buy">
                    <span className="nip-sale-lbl">You paid</span>
                    <input
                      type="number" step="0.01" min="0" placeholder="0.00"
                      value={chosen ? Number(buy).toFixed(2) : (raw ?? '')}
                      disabled={!!chosen}
                      onChange={e => setBuyPrices(p => ({ ...p, [sale.tradeId]: e.target.value }))}
                    />
                  </label>

                  <div className={`nip-sale-pnl${profit == null ? '' : profit >= 0 ? ' up' : ' down'}`}>
                    {profit == null ? 'n/a' : `${profit >= 0 ? '+' : '-'}${money(Math.abs(profit))}`}
                  </div>

                  <div className="nip-sale-acts">
                    <button
                      type="button" className="nip-sale-add"
                      disabled={!known}
                      onClick={() => chosen ? onCloseRow?.(sale, chosen.r) : onAdd?.(sale, typed)}
                    >
                      {chosen ? 'Mark sold' : 'Add to Sold'}
                    </button>
                    <button type="button" className="nip-sale-skip" onClick={() => onIgnore?.(sale)}>
                      Not mine
                    </button>
                  </div>
                </div>

                {/* Under the row rather than inside it: the warning is a sentence
                    and a dropdown, and squeezing both between the name and the
                    price boxes left neither readable. */}
                {candidates.length > 0 && (
                  <div className="nip-sale-match">
                    <span className="nip-sale-warn">
                      You have {candidates.length} live row{candidates.length !== 1 ? 's' : ''} for this
                      item. Close one instead of adding a second.
                    </span>
                    <select
                      value={target}
                      onChange={e => setTargets(t => ({ ...t, [sale.tradeId]: e.target.value }))}
                    >
                      {candidates.map(({ r, i }) => (
                        <option key={i} value={String(i)}>
                          Close: bought {money(r.buyPrice)}
                          {r.timestamp ? ` on ${new Date(r.timestamp).toLocaleDateString()}` : ''}
                        </option>
                      ))}
                      <option value="new">Add as a separate new row</option>
                    </select>
                  </div>
                )}

                {/* Mounted only while open, so a list of 75 sales makes no
                    requests until one is actually asked about. */}
                {open && (
                  <ItemHistoryTimeline
                    variant={variant}
                    item={{
                      name:      sale.name,
                      condition: sale.condition,
                      float:     sale.float,
                      paintSeed: sale.paintSeed,
                      assetId:   sale.assetId,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="nip-foot">
          {sales.length > 0 && (
            <div className="nip-dest">
              <button
                type="button" className="nip-add sold"
                onClick={addAllPriced}
                disabled={readyToAdd.length === 0}
                // Named rather than counted alone, because "add 12" gives no
                // way to check the 12 are the ones you meant.
                title={readyToAdd.length
                  ? readyToAdd.map(({ sale }) => sale.name).join('\n')
                  : 'Type what you paid on a row to enable this'}
              >
                <span className="nip-add-ico" aria-hidden="true">✓</span>
                Add {readyToAdd.length || ''} priced to Sold
              </button>
              <span className="nip-note tight">
                {readyToAdd.length === 0
                  ? 'Fill in "You paid" on a row to include it.'
                  : <>Files the {readyToAdd.length} row{readyToAdd.length !== 1 ? 's' : ''} you have priced
                      {sales.length > readyToAdd.length && <> and leaves the other {sales.length - readyToAdd.length} alone</>}.</>}
              </span>
            </div>
          )}

          <div className="nip-foot-end">
            <span className="nip-note">
              <strong>Add to Sold</strong> creates the row with the real sale price and date.{' '}
              <strong>Not mine</strong> stops the tracker asking about it again.
            </span>
            <button type="button" className="nip-dismiss" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

export function NewItemsModal({
  items, livePrices, settings, skinsData = [], trackedAssetIds, onAddToLive, onDismiss, variant = 'classic',
}) {
  const untracked = useMemo(
    () => items.filter(i => !trackedAssetIds?.has(i.assetId)),
    [items, trackedAssetIds]
  );

  const [selected, setSelected] = useState(() => new Set(untracked.map(i => i.assetId)));

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onDismiss?.(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onDismiss]);

  const toggle = assetId =>
    setSelected(prev => {
      const n = new Set(prev);
      n.has(assetId) ? n.delete(assetId) : n.add(assetId);
      return n;
    });

  const toggleAll = () =>
    setSelected(prev =>
      prev.size === untracked.length ? new Set() : new Set(untracked.map(i => i.assetId))
    );

  const handleAdd = (asInvestment) => {
    const toAdd = untracked
      .filter(i => selected.has(i.assetId))
      .map(i => ({ ...i, isInvestment: asInvestment }));
    onAddToLive(toAdd, Array.from(selected));
  };

  const canAdd = selected.size > 0;

  const body = (
    <div
      className={`nip-overlay ${variant}`}
      onMouseDown={e => { if (e.target === e.currentTarget) onDismiss?.(); }}
    >
      <div className="nip-panel" role="dialog" aria-modal="true" aria-label="New items detected">
        {/* Header */}
        <div className="nip-head">
          <div className="nip-head-txt">
            <div className="nip-title">New Items Detected</div>
            <div className="nip-sub">
              {untracked.length} new item{untracked.length !== 1 ? 's' : ''} appeared in your inventory.
              Select items, then choose where to add them.
            </div>
          </div>
          <button type="button" className="nip-x" onClick={onDismiss} aria-label="Close">×</button>
        </div>

        {/* Select-all bar */}
        <div className="nip-selbar">
          <button type="button" className="nip-selall" onClick={toggleAll}>
            {selected.size === untracked.length ? 'Deselect all' : 'Select all'}
          </button>
          <span className="nip-selcount">{selected.size} of {untracked.length} selected</span>
        </div>

        {/* Grid */}
        <div className="nip-body">
          {untracked.length === 0 ? (
            <div className="nip-empty">All new items are already in your tracker.</div>
          ) : (
            <div className="nip-grid">
              {untracked.map(item => (
                <NewItemCard
                  key={item.assetId}
                  item={item}
                  selected={selected.has(item.assetId)}
                  onToggle={toggle}
                  livePrices={livePrices}
                  skinsData={skinsData}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer — two destinations for the current selection */}
        <div className="nip-foot">
          <div className="nip-dest">
            <button type="button" className="nip-add live" onClick={() => handleAdd(false)} disabled={!canAdd}>
              <span className="nip-add-ico" aria-hidden="true">📊</span>
              Add {canAdd ? selected.size : ''} to Live
            </button>
            <button type="button" className="nip-add invest" onClick={() => handleAdd(true)} disabled={!canAdd}>
              <span className="nip-add-ico" aria-hidden="true">📈</span>
              Add {canAdd ? selected.size : ''} to Investments
            </button>
          </div>

          <div className="nip-foot-end">
            <span className="nip-note">
              <strong>Live</strong> tracks profit and loss in the main tracker.{' '}
              <strong>Investments</strong> tracks as a portfolio with a price chart.
            </span>
            <button type="button" className="nip-dismiss" onClick={onDismiss}>Dismiss</button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

function NewItemCard({ item, selected, onToggle, livePrices, skinsData = [] }) {
  const mhn      = item.market_hash_name || item.marketHashName || '';
  const baseName = stripCondition(mhn);
  const exterior = item.exterior || null;
  const condLabel = EXTERIOR_TO_CONDITION[exterior] || exterior || null;
  const price    = livePrices?.[mhn]?.starting_at?.price ?? null;
  // Inventory items normally arrive with a Steam icon; anything rebuilt from a
  // marketplace or a trade does not, and falls back to the database.
  const iconUrl  = item.iconUrl || item.icon_url
    || findSkinImage(skinsData, baseName, condLabel) || null;
  const rarityColor = item.rarityColor || 'rgba(255,255,255,0.12)';

  return (
    <div
      className={`nip-card${selected ? ' on' : ''}`}
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      onClick={() => onToggle(item.assetId)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(item.assetId); } }}
    >
      <div className="nip-rarity" style={{ background: rarityColor }} />

      <div className="nip-check" aria-hidden="true">{selected && '✓'}</div>

      <div className="nip-img">
        {iconUrl
          ? <img src={iconUrl} alt="" loading="lazy" />
          : <div className="nip-img-blank" />
        }
      </div>

      <div className="nip-info">
        <div className="nip-name">{baseName}</div>
        {condLabel && <div className="nip-cond">{condLabel}</div>}
        {price != null && <div className="nip-price">${price.toFixed(2)}</div>}
      </div>
    </div>
  );
}
